import { Button, Spinner } from "@fluentui/react-components";
import { useActivities } from "../hooks/useActivities";
import type { RefreshTokenFn } from "../../shared/api/fetchWithRefresh";
import { ActivityCard } from "./ActivityCard";
import styles from "./ApprovalsPanel.module.css";

interface ApprovalsPanelProps {
  /** MSAL bearer token for the backend (falls back to the Graph token upstream). */
  readonly apiToken: string | null;
  /** Silently re-acquires the bearer and retries on a 401. */
  readonly refreshTokens?: RefreshTokenFn;
}

/** Standalone tab that polls background-skill HITL decisions and lets the user
 *  approve / reject / give feedback. Separate from the inline chat flow. */
export function ApprovalsPanel({ apiToken, refreshTokens }: ApprovalsPanelProps) {
  const { activities, loading, error, submittingId, refresh, submit } = useActivities(apiToken, refreshTokens);

  let content;
  if (loading && activities.length === 0) {
    content = <Spinner label="Loading approvals…" />;
  } else if (activities.length === 0) {
    content = <p className={styles.empty}>No pending approvals.</p>;
  } else {
    content = (
      <ul className={styles.list}>
        {activities.map((activity) => (
          <ActivityCard
            key={activity.id}
            activity={activity}
            submitting={submittingId === activity.id}
            onSubmit={(decision) => void submit(activity, decision)}
          />
        ))}
      </ul>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <Button appearance="subtle" size="small" onClick={refresh} disabled={loading}>
          Refresh
        </Button>
      </div>

      {error && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}

      {content}
    </div>
  );
}
